import { useDeferredValue, useMemo, useState } from 'react'

import { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { useTranslation } from 'react-i18next'

import { protoDateToCivilDate, requireCivilDate } from '~/models/civilDate'
import { MasterCrudTabShell } from '~/pages/AdminOcctoMaster/shell/MasterCrudTabShell'
import { useMasterCrudTab } from '~/pages/AdminOcctoMaster/shell/useMasterCrudTab'
import { CreateDemandSuppressionPlanMasterModal } from '~/pages/AdminOcctoMaster/tabs/DemandSuppressionPlanMaster/components/CreateDemandSuppressionPlanMasterModal'
import { DeleteDemandSuppressionPlanMasterDialog } from '~/pages/AdminOcctoMaster/tabs/DemandSuppressionPlanMaster/components/DeleteDemandSuppressionPlanMasterDialog'
import { DemandSuppressionPlanMasterTable } from '~/pages/AdminOcctoMaster/tabs/DemandSuppressionPlanMaster/components/DemandSuppressionPlanMasterTable'
import { EditDemandSuppressionPlanMasterModal } from '~/pages/AdminOcctoMaster/tabs/DemandSuppressionPlanMaster/components/EditDemandSuppressionPlanMasterModal'
import { useListDemandSuppressionMasters } from '~/pages/AdminOcctoMaster/tabs/DemandSuppressionPlanMaster/hooks/useListDemandSuppressionMasters'
import { demandSuppressionPlanMasterCsvColumns } from '~/pages/AdminOcctoMaster/tabs/DemandSuppressionPlanMaster/models/csvColumns'
import type { DemandSuppressionMasterRow } from '~/pages/AdminOcctoMaster/tabs/DemandSuppressionPlanMaster/types'
import { ExpiredFilterToggle } from '~/pages/AdminOcctoMaster/tabs/_common/components/ExpiredFilterToggle'
import { MasterFilterToolbar } from '~/pages/AdminOcctoMaster/tabs/_common/components/MasterFilterToolbar'

function filterRows(
  rows: DemandSuppressionMasterRow[],
  areaCode: AreaCode,
  searchText: string,
): DemandSuppressionMasterRow[] {
  const trimmed = searchText.trim().toLowerCase()
  return rows.filter((row) => {
    if (areaCode !== AreaCode.UNSPECIFIED && row.areaCode !== areaCode) {
      return false
    }
    if (trimmed === '') {
      return true
    }
    return (
      row.submitterCode.toLowerCase().includes(trimmed) ||
      (row.note?.toLowerCase().includes(trimmed) ?? false)
    )
  })
}

function convertItem(
  item: import('@ebalance/proto/bffuser/v1/occto_master_model_pb').DemandSuppressionMasterListItem,
): DemandSuppressionMasterRow {
  return {
    id: item.id,
    submitterCode: item.submitterCode,
    areaCode: item.areaCode,
    note: item.note,
    contractCount: item.contractCount,
    partnerBgCount: item.partnerBgCount,
    updatedBy: item.updatedBy,
    updatedAt: item.updatedAt,
    validFrom: requireCivilDate(
      protoDateToCivilDate(item.validFrom),
      `DemandSuppressionMasterListItem.validFrom (id=${item.id})`,
    ),
    validTo: protoDateToCivilDate(item.validTo),
  }
}

/**
 * 需要抑制計画マスタ タブ。
 * 一覧 + CRUD モーダルを表示する。
 * 旧 DemandSuppressionPartnersTab (= 取引先一覧) を計画マスタ CRUD に作り替えたタブ。
 */
export const DemandSuppressionPlanMasterTab = (): React.ReactElement => {
  const { t } = useTranslation()
  const { data, isLoading, isError, error } = useListDemandSuppressionMasters()

  const [selectedAreaCode, setSelectedAreaCode] = useState<AreaCode>(
    AreaCode.UNSPECIFIED,
  )
  const [searchText, setSearchText] = useState('')
  const deferredSearchText = useDeferredValue(searchText)

  const rows = useMemo(
    () => (data !== undefined ? data.items.map(convertItem) : []),
    [data],
  )
  const filteredRows = useMemo(
    () => filterRows(rows, selectedAreaCode, deferredSearchText),
    [rows, selectedAreaCode, deferredSearchText],
  )

  const subject = t(
    'page:adminOcctoMaster.common.subject.demandSuppressionPlan',
  )
  const tab = useMasterCrudTab<DemandSuppressionMasterRow>({
    data,
    isLoading,
    isError,
    error,
    errorKeyPrefix:
      'page:adminOcctoMaster.demandSuppressionPlanMaster.serverError',
    filteredRows,
    csv: {
      columns: demandSuppressionPlanMasterCsvColumns(),
      fileBase: 'demand-suppression-plan',
    },
    expiry: {
      getPeriod: (row) => ({
        validFrom: row.validFrom,
        validTo: row.validTo,
      }),
      subject,
    },
  })

  const deleteRow = tab.modalState.kind === 'delete' ? tab.modalState.row : null

  return (
    <MasterCrudTabShell
      createModal={
        // 条件 mount (閉じたら form ごと破棄): 常時 mount + isOpen 切替だと
        // 保存成功時の reset() 直後に mount 中の field array 行入力が RHF の
        // 再 register で直前の値を書き戻し、次回 open で行が残留する (#6072、
        // 詳細は DemandProcurementPlanMaster/index.tsx の同コメント)。
        tab.modalState.kind === 'create' ? (
          <CreateDemandSuppressionPlanMasterModal
            isOpen
            onClose={tab.closeModal}
          />
        ) : null
      }
      csvExportDisabledReason={tab.csvExportDisabledReason}
      deleteDialog={
        deleteRow !== null ? (
          <DeleteDemandSuppressionPlanMasterDialog
            row={deleteRow}
            errorMessage={tab.deleteError?.message}
            isOpen={tab.modalState.kind === 'delete'}
            onClose={tab.closeModal}
            onError={(message) => {
              tab.setDeleteError({ message, retriable: true })
            }}
          />
        ) : null
      }
      editModal={
        tab.modalState.kind === 'edit' ? (
          <EditDemandSuppressionPlanMasterModal
            row={tab.modalState.row}
            isOpen
            onClose={tab.closeModal}
          />
        ) : null
      }
      filterToolbar={
        <MasterFilterToolbar
          onSearchTextChange={setSearchText}
          onSelectArea={setSelectedAreaCode}
          searchPlaceholder={t(
            'page:adminOcctoMaster.demandSuppressionPlanMaster.toolbar.searchPlaceholder',
          )}
          searchText={searchText}
          selectedAreaCode={selectedAreaCode}
          trailing={
            <ExpiredFilterToggle
              expiredCount={tab.expiredCount}
              includeExpired={tab.includeExpired}
              onChange={tab.setIncludeExpired}
              subject={subject}
            />
          }
        />
      }
      onAddIndividual={tab.openCreate}
      onCsvExport={tab.csvExportEnabled ? tab.handleCsvExport : undefined}
      showInitialLoading={tab.showInitialLoading}
      table={
        <DemandSuppressionPlanMasterTable
          emptyLabel={tab.emptyLabel}
          errorLabel={tab.errorLabel}
          isLoading={isLoading}
          onDelete={tab.openDelete}
          onEdit={tab.openEdit}
          rows={tab.visibleRows}
          today={tab.today}
        />
      }
    />
  )
}
